<script setup lang="ts">
const { isOpen, close } = useGasCalculator();

const referencePrice = ref<number | undefined>(undefined);
const mintPrice = ref<number | undefined>(undefined);
const otcPrice = ref<number>(0);
const marketplaceFee = ref(1); // Opensea fee
const royaltyFee = ref(5);
const gasAggressivity = ref(50);

const totalFees = computed(() => marketplaceFee.value + royaltyFee.value);

const netResale = computed(() => {
  if (!referencePrice.value) return 0;
  return referencePrice.value * (1 - totalFees.value / 100);
});

const spread = computed(() => netResale.value - (mintPrice.value || 0) - otcPrice.value);

const gasBudget = computed(() => {
  if (spread.value <= 0) return 0;
  return spread.value * (gasAggressivity.value / 100);
});

const potentialProfit = computed(() => {
  if (spread.value <= 0) return 0;
  return spread.value - gasBudget.value;
});

const isNegativeSpread = computed(() => {
  const hasInput = referencePrice.value !== undefined && mintPrice.value !== undefined;
  return hasInput && spread.value <= 0;
});

function reset() {
  referencePrice.value = undefined;
  mintPrice.value = undefined;
  otcPrice.value = 0;
  royaltyFee.value = 5;
  gasAggressivity.value = 50;
}

function formatUsd(value: number): string {
  if (value === 0) return "$0.00";
  if (value < 0.01) return "<$0.01";
  return `$${value.toFixed(2)}`;
}
</script>

<template>

<UModal
  :model-value="isOpen"
  @update:model-value="close"
  :ui="{
    shadow: 'shadow-none',
    background: '!bg-transparent',
    width: 'w-full sm:w-[24rem]',
    padding: 'p-0',
  }">

  <GenericPanel class="overflow-hidden">

    <GenericModalHeader
      :model-value="isOpen"
      @update:model-value="close"
      title="Gas Calculator" />

    <div class="px-4 py-4 space-y-4">

      <!-- Mint price -->
      <GenericLabel label="Mint price">
        <UInput
          v-model.number="mintPrice"
          type="number"
          step="any"
          min="0"
          size="sm"
          placeholder="0"
          input-class="text-center"
          class="w-full mt-0.5">
          <template #trailing>
            <span class="text-xs text-gray-400">USD</span>
          </template>
        </UInput>
      </GenericLabel>

      <!-- Reference price -->
      <GenericLabel label="Sell Price">
        <UInput
          v-model.number="referencePrice"
          type="number"
          step="any"
          min="0"
          size="sm"
          placeholder="0"
          input-class="text-center"
          class="w-full mt-0.5">
          <template #trailing>
            <span class="text-xs text-gray-400">USD</span>
          </template>
        </UInput>
      </GenericLabel>

      <!-- OTC price -->
      <GenericLabel label="OTC Price">
        <UInput
          v-model.number="otcPrice"
          type="number"
          step="any"
          min="0"
          size="sm"
          placeholder="0"
          input-class="text-center"
          class="w-full mt-0.5">
          <template #trailing>
            <span class="text-xs text-gray-400">USD</span>
          </template>
        </UInput>
      </GenericLabel>

      <div class="flex items-center space-x-2 w-full">

        <!-- Marketplace fee -->
        <GenericLabel class="flex-1" label="Opensea fee">
          <UInput
            v-model.number="marketplaceFee"
            type="number"
            step="0.5"
            min="0"
            max="100"
            size="sm"
            color="gray"
            placeholder="2.5"
            disabled
            input-class="text-center"
            class="w-full mt-0.5">
            <template #trailing>
              <span class="text-xs text-gray-400">%</span>
            </template>
          </UInput>
        </GenericLabel>

        <!-- Royalty fee -->
        <GenericLabel class="flex-1" label="Royalty fee">
          <UInput
            v-model.number="royaltyFee"
            type="number"
            step="0.5"
            min="0"
            max="100"
            size="sm"
            placeholder="5"
            input-class="text-center"
            class="w-full mt-0.5">
            <template #trailing>
              <span class="text-xs text-gray-400">%</span>
            </template>
          </UInput>
        </GenericLabel>

      </div>

      <!-- Gas aggressivity slider -->
      <GenericLabel>
        <template #label>
          <div class="flex items-center justify-between w-full">
            <span>Gas Aggressivity</span>
            <span class="font-mono text-gray-700 dark:text-gray-200">{{ gasAggressivity }}%</span>
          </div>
        </template>
        <URange
          v-model="gasAggressivity"
          :min="0"
          :max="100"
          :step="1"
          size="sm"
          class="w-full mt-1" />
      </GenericLabel>

      <!-- Warning: negative spread -->
      <div
        v-if="isNegativeSpread"
        class="flex items-center space-x-2 border border-amber-900/10 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/30 p-2 rounded-[4px]">
        <UIcon name="i-mingcute-alert-line" class="text-amber-900 dark:text-amber-400 size-4 shrink-0" />
        <p class="text-xs font-medium text-orange-950 dark:text-amber-200">
          Mint is unprofitable after fees regardless of gas.
        </p>
      </div>

      <!-- Output -->
      <div v-else class="flex items-center space-x-2 w-full">
        <div class="flex-1 border border-gray-200 dark:border-gray-700 rounded-[4px] p-3 space-y-1">
          <p class="text-xs uppercase font-medium text-gray-500 dark:text-gray-400">gas budget</p>
          <span class="text-lg font-semibold text-gray-900 dark:text-white font-mono">
            {{ formatUsd(gasBudget) }}
          </span>
        </div>
        <div class="flex-1 border border-gray-200 dark:border-gray-700 rounded-[4px] p-3 space-y-1">
          <p class="text-xs uppercase font-medium text-gray-500 dark:text-gray-400">potential profit</p>
          <span class="text-lg font-semibold text-gray-900 dark:text-white font-mono">
            {{ formatUsd(potentialProfit) }}
          </span>
        </div>
      </div>

    </div>

    <!-- Footer -->
    <div class="flex justify-end sm:space-x-2 space-x-4 px-4 pb-4">
      <UButton
        color="white"
        icon="i-tabler-refresh"
        label="Reset"
        @click="reset" />
      <UButton
        color="white"
        label="Close"
        @click="close" />
    </div>

  </GenericPanel>

</UModal>

</template>
